import { useQuery } from '@tanstack/react-query';
import type { CurrentClosePeriodDto } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiResponse } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

export type { CurrentClosePeriodDto };

/**
 * CPH-001: Resolves the campaign's currently-open `FilingDeadline` for the
 * Close-the-period hub. "No open period" is the routine "nothing to close
 * right now" empty state — the backend reports it as a 200 with an empty/null
 * body (not a 404, which the browser would log as a failed request on the
 * close pages), and this hook resolves it to `null` so `CloseLayout` renders
 * an honest empty state. Any non-2xx response (route mismatch, capability
 * revoked, server error) throws so the empty state cannot silently mask a
 * real failure.
 */
export function useCurrentClosePeriod() {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.finance.close.current(),
    queryFn: async (): Promise<CurrentClosePeriodDto | null> => {
      const response = await apiResponse('/api/finance/close/current');
      if (!response.ok) {
        throw new Error('Failed to fetch current close period');
      }
      // Empty body (204-style) or the literal `null` (200 + Ok(null)) both
      // mean "no open period" → null. A present body parses to the DTO.
      const text = await response.text();
      return text ? (JSON.parse(text) as CurrentClosePeriodDto) : null;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    // 30 seconds — current period rarely changes within a treasurer session.
    staleTime: 30_000,
  });
}
